import { Button, Dialog, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import { generateCsv, mkConfig, download } from "export-to-csv";
import { useCallback, useEffect, useState } from "react";
import AutoCompleteComponent from "../../Components/AutoComplete/AutoComplete";
import MonthPicker from "../../Components/DatePickers/MonthPicker";
import Breadcrumb from "../../Components/Navigation/Breadcrumbs/Breadcrumbs";
import BillingReportCell from "../../Components/Table/BillingReportCell";
import SortableTable from "../../Components/Table/SortableTable";
import {
  getBillingReportNotificationSettings,
  getClientBillingHistory,
} from "../../Services/API/BillingReport";
import { getAllClients } from "../../Services/API/ClientManagement";
import { Client } from "../../Types/ClientManagement";
import UpdateBillingSettingsModal from "../../Components/Modal/UpdateBillingSettingsModal";
import { Settings } from "@material-ui/icons";
import {
  ClientBillingReport,
  ClientBillingReportDisplay,
  ClientBillingReportSettings,
} from "../../Types/ClientBilling";
import Signifier from "../../Components/Signifier";

const columns = [
  { label: "Client", propName: "clientName" },
  { label: "Created For", propName: "createdFor" },
  { label: "Created On", propName: "dateCreated" },
  { label: "Total", propName: "totalBill" },
];

const useStyles = makeStyles((theme) => ({
  root: {
    width: "90%",
    marginBottom: "50px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  tableSection: {
    paddingBottom: "20px",
  },
  autocomplete: {
    maxWidth: "20vw",
    paddingBottom: "20px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      maxWidth: "100%",
    },
  },
  clearButton: {
    marginTop: "15px",
    marginLeft: "15px",
    [theme.breakpoints.down("sm")]: {
      marginLeft: "0",
    },
  },
  settingsSection_filters: {
    maxWidth: "30vw",
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      paddingBottom: "20px",
    },
  },
  dialog: {
    "& .MuiDialogContent-root": {
      padding: theme.spacing(2),
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
    "& .MuiDialogActions-root": {
      paddingTop: theme.spacing(1),
      paddingRight: theme.spacing(3),
      paddingBottom: theme.spacing(2),
      paddingLeft: theme.spacing(3),
    },
  },
}));

function BillingReportsPage() {
  const classes = useStyles();
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [date, setDate] = useState<string | null>(null);

  const [reports, setReports] = useState<ClientBillingReportDisplay[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);

  const [showSignifier, setShowSignifier] = useState(false);
  const [signifierMessage, setSignifierMessage] = useState("Billing Settings Updated");

  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savedSettings, setSavedSettings] = useState<ClientBillingReportSettings>(
    {} as ClientBillingReportSettings
  );
  const [showUpdateSettingsError, setShowUpdateSettingsError] = useState(false);
  const [updateSettingsErrorMessage, setUpdateSettingsErrorMessage] = useState(
    "Error Updating Billing Settings"
  );

  axios.defaults.headers.common["client"] = selectedClient?.id;

  const disableExport =
    reports.length === 0 ||
    (selectedClient != null &&
      reports.find((d) => d.clientId === selectedClient.centralClientId) === undefined);

  const handleClientChange = (event: any, value: Client) => {
    setSelectedClient(value);
  };

  const handleDateChange = (date: Date, value?: string | null | undefined) => {
    setDate(new Date(date).toLocaleDateString("en-US"));
  };

  const handleOpenBillingSettingsModal = () => {
    setSettingsModalOpen(true);
  };

  const handleSetUpdatedSettings = () => {
    getReportSettings();
    setSignifierMessage("Billing Settings Updated");
    setShowSignifier(true);
  };

  const handleExportAllData = useCallback(() => {
    generateCsvExportData(selectedClient, reports);
    setSignifierMessage("Data Exported");
    setShowSignifier(true);
  }, [selectedClient, reports]);

  useEffect(() => {
    getAllClients()
      .then((clients) =>
        setAllClients((_) =>
          clients.sort((a, b) => compareStrings(a.shortName.toLowerCase(), b.shortName.toLowerCase()))
        )
      )
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    if (allClients.length > 0 && !loadingReports) {
      setLoadingReports(true);
      getClientBillingHistory(selectedClient?.centralClientId, date)
        .then((reports) => setReports((_) => setupReportsForDisplay(allClients, reports)))
        .catch((err) => console.error(err))
        .finally(() => setLoadingReports(false));
    } else {
      setReports([]);
      setLoadingReports(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, selectedClient, allClients]);

  const getReportSettings = useCallback(() => {
    setLoadingSettings(true);
    getBillingReportNotificationSettings()
      .then((res) => {
        setSavedSettings(res);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoadingSettings(false);
      });
  }, []);

  useEffect(() => {
    if (!savedSettings.fromName) {
      getReportSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSettings.fromName]);

  return (
    <div className="page-container">
      <Signifier
        showSignifier={showSignifier}
        setShowSignifier={setShowSignifier}
        message={signifierMessage}
        error={showUpdateSettingsError}
        errorMessage={updateSettingsErrorMessage}
      />
      <Breadcrumb />
      <h1 className="page-title">Billing Reports</h1>
      <div className={classes.root}>
        <div className={classes.autocomplete}>
          <AutoCompleteComponent
            dropdownItems={allClients}
            propertyName="shortName"
            label="All Clients"
            onChange={handleClientChange}
            selectOption={(option, value) => option.id === value.id}
            currValue={undefined}
          />
        </div>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={12}>
            <MonthPicker value={date} handleChange={handleDateChange} />
            {date !== null && (
              <Button
                className={classes.clearButton}
                type="submit"
                variant="outlined"
                onClick={() => setDate(null)}
              >
                X
              </Button>
            )}
          </Grid>
          <Grid item xs={12} sm={6} style={{ display: "flex", gap: "20px" }}>
            <Button
              style={{
                backgroundColor: disableExport ? "rgba(0,0,0,0.15)" : "green",
                color: disableExport ? "rgba(0,0,0,0.5)" : "white",
                transition: "all 0.3s ease-in-out",
              }}
              type="submit"
              variant="contained"
              onClick={handleExportAllData}
              disabled={disableExport}
            >
              Export All Data
            </Button>
            <Button
              onClick={handleOpenBillingSettingsModal}
              variant="outlined"
              endIcon={<Settings />}
              color="secondary"
              style={{}}
            >
              Edit Settings
            </Button>
            <Dialog
              className={classes.dialog}
              maxWidth="md"
              open={settingsModalOpen}
              onClose={(event, reason) => {
                if (reason === "backdropClick") {
                  setSettingsModalOpen(false);
                }
              }}
            >
              <UpdateBillingSettingsModal
                savedSettings={savedSettings}
                loadingSettings={loadingSettings}
                setLoadingSettings={setLoadingSettings}
                updateSettingsErrorMessage={updateSettingsErrorMessage}
                setShowUpdateSettingsError={setShowUpdateSettingsError}
                setUpdateSettingsErrorMessage={setUpdateSettingsErrorMessage}
                setShowModal={setSettingsModalOpen}
                refreshSettings={handleSetUpdatedSettings}
              />
            </Dialog>
          </Grid>
        </Grid>
      </div>
      <div className={classes.tableSection}>
        <SortableTable
          columns={columns}
          tableData={reports}
          rowComponent={BillingReportCell}
          loading={loadingReports}
          hideSearch={false}
          defaultSortIndex={undefined}
          setTableData={undefined}
          reverse={undefined}
          pagination={undefined}
          totalItemCount={undefined}
        />
      </div>
    </div>
  );
}

const convertDate = (date: string | number | Date) => {
  var options = { month: "long", year: "numeric", day: "numeric" } as Intl.DateTimeFormatOptions;
  var newDate = new Date(date).toLocaleDateString("en-US", options);

  return newDate;
};

const compareStrings = (a: string, b: string) => {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
};

function getCsvOptions(client: Client | null) {
  const title = client !== null ? client.shortName + " Billing Reports" : "All Billing Reports";
  const filename = title.replace(/\s/g, "_");
  return mkConfig({
    filename,
    quoteStrings: true,
    showTitle: true,
    title,
    columnHeaders: [
      { key: "clientName", displayLabel: "Client Name" },
      { key: "createdFor", displayLabel: "Created For" },
      { key: "dateCreated", displayLabel: "Date Created" },
      { key: "baseBill", displayLabel: "Base Bill" },
      { key: "totalBill", displayLabel: "Total Bill" },
      { key: "candidateCount", displayLabel: "Candidate Count" },
      { key: "candidateQuota", displayLabel: "Candidate Quota" },
      { key: "candidateOverageBill", displayLabel: "Candidate Overage Bill" },
      { key: "candidateRate", displayLabel: "Candidate Rate" },
      { key: "emailCount", displayLabel: "Email Count" },
      { key: "emailQuota", displayLabel: "Email Quota" },
      { key: "emailOverageBill", displayLabel: "Email Overage Bill" },
      { key: "emailRate", displayLabel: "Email Rate" },
      { key: "textCount", displayLabel: "Text Count" },
      { key: "textQuota", displayLabel: "Text Quota" },
      { key: "textOverageBill", displayLabel: "Text Overage Bill" },
      { key: "textRate", displayLabel: "Text Rate" },
    ],
  });
}

const setupReportsForDisplay = (allClients: Client[], bills: ClientBillingReport[]) => {
  const clientNameLookup = allClients.reduce((acc, curr) => {
    acc[curr.centralClientId] = curr.shortName;
    return acc;
  }, {} as Record<string, string>);
  return bills
    .filter((bill) => clientNameLookup[bill.clientId] !== undefined)
    .map((bill) => {
      const createdFor = new Date(bill.startDateUtc).toLocaleString("default", { month: "long" });
      return {
        ...bill,
        clientName: clientNameLookup[bill.clientId],
        dateCreated: convertDate(bill.dateCreated),
        createdFor,
        totalBill: "$" + bill.totalBill.toFixed(2).toString(),
      } as ClientBillingReportDisplay;
    });
};

function generateCsvExportData(selectedClient: Client | null, data: ClientBillingReportDisplay[]) {
  if (data.length > 0) {
    const csvExportData = data.map((d) => ({
      clientName: d.clientName,
      createdFor: d.createdFor,
      dateCreated: d.dateCreated,
      baseBill: d.baseBill,
      totalBill: d.totalBill,
      candidateCount: d.candidateCount,
      candidateQuota: d.candidateQuota,
      candidateOverageBill: d.candidateOverageBill,
      candidateRate: d.candidateRate,
      emailCount: d.emailCount,
      emailQuota: d.emailQuota,
      emailOverageBill: d.emailOverageBill,
      emailRate: d.emailRate,
      textCount: d.textCount,
      textQuota: d.textQuota,
      textOverageBill: d.textOverageBill,
      textRate: d.textRate,
    }));
    const options = getCsvOptions(selectedClient);
    const csv = generateCsv(options)(csvExportData);
    return download(options)(csv);
  }
}

export default BillingReportsPage;
